/**
 * What adding or removing one name does to a feature's audience.
 *
 * Separate from the panel because it is the one part with real decisions in it,
 * and those decisions are easier to get wrong than they look: taking the last
 * person off a "People and groups" feature means **Off**, not an empty list the
 * server would refuse, and adding a name to a `custom` feature has to pick an
 * audience for rules that never had one.
 *
 * The server owns the translation from an audience to rows
 * (`backend/flags_admin.py`); this owns only the small question of which
 * audience the next click means.
 */

import type { AdminFlag, FlagAudience } from '../../api/types.ts';

export type Choice = Exclude<FlagAudience, 'custom'>;
export interface PrincipalRef {
  kind: 'user' | 'group';
  id: string;
  name: string;
}

/** The principals a flag's rules name, on the allow side or the exception side. */
export function principalsOf(flag: AdminFlag, wanted: boolean): PrincipalRef[] {
  const out: PrincipalRef[] = [];
  for (const rule of flag.rules) {
    if (rule.value !== wanted) continue;
    if (rule.principal_kind === 'user' && rule.user_id) {
      out.push({ kind: 'user', id: rule.user_id, name: rule.user_name ?? 'Deleted account' });
    } else if (rule.principal_kind === 'group' && rule.group_id) {
      out.push({ kind: 'group', id: rule.group_id, name: rule.group_name ?? 'Deleted group' });
    }
  }
  return out;
}

export function samePrincipal(a: PrincipalRef, b: PrincipalRef): boolean {
  return a.kind === b.kind && a.id === b.id;
}

/**
 * The audience after naming somebody.
 *
 * Naming somebody to *allow* is the same gesture as choosing "People and
 * groups" - nobody opens that picker meaning anything else. An exception keeps
 * whatever audience is there, except on a `custom` flag, where there is no
 * audience to keep and "everyone except…" is the only reading of an exception.
 */
export function audienceAfterAdd(current: FlagAudience, deny: boolean): Choice {
  if (!deny) return 'targeted';
  return current === 'custom' ? 'everyone' : current;
}

/**
 * The audience after taking a name off.
 *
 * An allow list emptied by the last removal is "Off": the alternative is asking
 * the server for a targeted audience with nobody in it, which it refuses - and
 * rightly, since that is Off with extra steps.
 */
export function audienceAfterRemove(current: FlagAudience, allowedLeft: number): Choice {
  if (current === 'custom') return allowedLeft ? 'targeted' : 'off';
  if (current === 'targeted' && !allowedLeft) return 'off';
  return current;
}
